<template>
  <nav class="navbar navbar-expand-lg navbar-light bg-white shadow-sm sticky-top">
    <div class="container">
      <!-- Logo -->
      <router-link class="navbar-brand fw-bold text-primary" to="/">
        <HeartIcon class="nav-icon me-2" />FemWellness
      </router-link>

      <!-- Mobile Toggle -->
      <button
        class="navbar-toggler"
        type="button"
        data-bs-toggle="collapse"
        data-bs-target="#navbarNav"
        @click="toggleNavbar"
      >
        <span class="navbar-toggler-icon"></span>
      </button>

      <!-- Navigation Links -->
      <div class="collapse navbar-collapse" id="navbarNav">
        <ul class="navbar-nav me-auto">
          <li v-for="navItem in navigationItems" :key="navItem.path" class="nav-item">
            <router-link 
              class="nav-link" 
              :to="navItem.path" 
              :class="{ active: $route.path === navItem.path }"
            >
              <component :is="navItem.icon" class="nav-icon me-1" />
              {{ navItem.name }}
            </router-link>
          </li>
        </ul>

        <!-- User Actions -->
        <div class="d-flex">
          <router-link v-if="!isAuthenticated" to="/login" class="btn btn-outline-primary me-2">
            Login
          </router-link>
          <router-link v-if="!isAuthenticated" to="/register" class="btn btn-primary">
            Sign Up
          </router-link>
          <div v-if="isAuthenticated" class="dropdown">
            <button
              class="btn btn-outline-primary dropdown-toggle"
              type="button"
              data-bs-toggle="dropdown"
            >
              <UserIcon class="nav-icon me-1" />
              {{ getRoleDisplayName() }}{{ user?.displayName || user?.email?.split('@')[0] || 'User' }}
            </button>
            <ul class="dropdown-menu">
              <li class="dropdown-header">
                <small class="text-muted">{{ userRole?.toUpperCase() || 'GUEST' }}</small>
              </li>
              <li><hr class="dropdown-divider" /></li>
              <li><router-link class="dropdown-item" to="/profile">
                <UserIcon class="nav-icon me-2" />Profile
              </router-link></li>
              <li><router-link class="dropdown-item" to="/settings">
                <SettingsIcon class="nav-icon me-2" />Settings
              </router-link></li>
              <li><hr class="dropdown-divider" /></li>
              <li><button class="dropdown-item" @click="logout">
                <LogoutIcon class="nav-icon me-2" />Logout
              </button></li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
import { computed } from 'vue'
import { useFirebaseAuth } from '@/composables/useFirebaseAuth'
import { getNavigationForRole } from '@/utils/roles'
import { HeartIcon, UserIcon, SettingsIcon, LogoutIcon } from '@/utils/icons'

export default {
  name: 'NavBar',
  components: {
    HeartIcon,
    UserIcon,
    SettingsIcon,
    LogoutIcon,
  },
  setup() {
    const { user, userRole, isAuthenticated, logout: firebaseLogout } = useFirebaseAuth()
    
    // Get navigation items based on user role
    const navigationItems = computed(() => {
      return getNavigationForRole(userRole.value)
    })
    
    return { 
      user, 
      userRole,
      isAuthenticated, 
      firebaseLogout,
      navigationItems
    }
  },
  methods: {
    toggleNavbar() {
      // Bootstrap handles this automatically
    },
    async logout() {
      await this.firebaseLogout()
      this.$router.push('/')
    },
    getRoleDisplayName() {
      if (!this.userRole) return 'Guest'
      switch (this.userRole) {
        case 'admin':
          return 'Admin'
        case 'doctor':
          return 'Dr.'
        case 'user':
        default:
          return ''
      }
    }
  },
}
</script>

<style scoped>
.navbar {
  z-index: 1030;
}

.dropdown-menu {
  z-index: 1050;
}

.navbar-brand {
  font-size: 1.5rem;
  color: var(--primary-color) !important;
}

.nav-link {
  font-weight: 500;
  transition: all 0.3s ease;
}

.nav-link:hover {
  color: var(--primary-color) !important;
}

.nav-link.active {
  color: var(--primary-color) !important;
  font-weight: 600;
}

.btn-primary {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}

.btn-outline-primary {
  color: var(--primary-color);
  border-color: var(--primary-color);
}

.btn-outline-primary:hover {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}

/* Heroicons styling */
.nav-icon {
  width: 1.25rem;
  height: 1.25rem;
  stroke-width: 2;
}

@media (max-width: 991px) {
  .navbar-nav {
    margin-top: 1rem;
  }

  .d-flex {
    margin-top: 1rem;
  }
}
</style>
